import { useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { AircraftType } from '../../types';
import api from '../../services/api';
import { fleetApi } from '../../services/fleetApi';
import { telemetryApi } from '../../services/telemetryApi';
import { contactsApi } from '../../services/contactsApi';
import type { CustomerFollowUp } from '../../services/customerApi';
import type { AircraftFlightInfoMap } from './types';

interface UseDashboardPollingParams {
  liveAircrafts: AircraftType[];
  filteredInitialAircrafts: AircraftType[];
  setLiveAircrafts: Dispatch<SetStateAction<AircraftType[]>>;
  setMyFollowUps: Dispatch<SetStateAction<(CustomerFollowUp & { customer: { id: string; name: string; company?: string; status: string } })[]>>;
  setAircraftFlightInfo: Dispatch<SetStateAction<AircraftFlightInfoMap>>;
  setWeatherData: Dispatch<SetStateAction<{ temp?: string; wind?: string; conditions?: string } | null>>;
}

export const useDashboardPolling = ({
  liveAircrafts,
  filteredInitialAircrafts,
  setLiveAircrafts,
  setMyFollowUps,
  setAircraftFlightInfo,
  setWeatherData,
}: UseDashboardPollingParams) => {
  const toIsoOrNull = (value: any): string | null => {
    if (!value) return null;
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return null;
    return dt.toISOString();
  };

  const pickTime = (obj: any, keys: string[]): string | null => {
    for (const key of keys) {
      const val = toIsoOrNull(obj?.[key]);
      if (val) return val;
    }
    return null;
  };

  const isSameLocalDay = (iso: string, ref: Date) => {
    const dt = new Date(iso);
    return (
      dt.getFullYear() === ref.getFullYear() &&
      dt.getMonth() === ref.getMonth() &&
      dt.getDate() === ref.getDate()
    );
  };

  const resolveCrewNames = (leg: any) => {
    const crew = Array.isArray(leg?.CrewAssignment) ? leg.CrewAssignment : [];
    const toName = (u: any) => `${u?.firstName || ''} ${u?.lastName || ''}`.trim() || u?.email || '';
    const pic = crew.find((c: any) => c?.role === 'PIC' || c?.role === 'CAPTAIN' || c?.role === 'PILOT');
    const sic = crew.find((c: any) => c?.role === 'SIC' || c?.role === 'FO' || c?.role === 'COPILOT' || c?.role === 'CO_PILOT');
    return {
      pilotName: pic?.User ? toName(pic.User) : undefined,
      coPilotName: sic?.User ? toName(sic.User) : undefined,
      pilotId: pic?.User?.id,
      coPilotId: sic?.User?.id,
    };
  };

  const scoreLegForAircraft = (leg: any, infoForTail: any, now: Date) => {
    const dep = pickTime(leg, ['offBlock', 'offBlockAt', 'departureOffBlockAt', 'scheduledDeparture', 'departureTime', 'etd']) ||
      pickTime(leg, ['takeoff', 'takeoffAt', 'departureTakeoffAt']);
    const arr = pickTime(leg, ['onBlock', 'onBlockAt', 'arrivalOnBlockAt', 'landing', 'landingAt', 'arrivalLandingAt', 'eta', 'arrivalTime']);
    const routeText = `${leg?.arrivalICAO || leg?.to || ''} ${leg?.arrivalName || ''}`.toLowerCase();
    const destText = `${infoForTail?.destination || ''}`.toLowerCase();
    const locText = `${infoForTail?.lastLocation || ''}`.toLowerCase();
    let score = 0;

    if (leg?.status === 'IN_FLIGHT' || leg?.status === 'ACTIVE' || leg?.status === 'SCHEDULED') score += 120;
    if (dep && isSameLocalDay(dep, now)) score += 80;
    if (arr && isSameLocalDay(arr, now)) score += 60;
    if (destText && routeText && routeText.includes(destText)) score += 45;
    if (locText && routeText && routeText.includes(locText)) score += 20;
    if (arr) score += Math.min(40, Math.max(0, (new Date(arr).getTime() - now.getTime()) / 60000 < 0 ? 25 : 5));
    if (dep) score += Math.min(30, Math.max(0, (now.getTime() - new Date(dep).getTime()) / 60000 < 0 ? 5 : 15));
    return score;
  };

  useEffect(() => {
    contactsApi.getMyFollowUps().then(setMyFollowUps).catch(() => {});
  }, [setMyFollowUps]);

  useEffect(() => {
    let isMounted = true;
    const fetchFlightInfo = async () => {
      if (!isMounted) return;

      try {
        const { default: lazyApi } = await import('../../services/api');
        const info: AircraftFlightInfoMap = {};

        const metaPromises = liveAircrafts.map((ac) =>
          telemetryApi.getTripMetadata(ac.tailNumber).then((meta) => ({ tailNumber: ac.tailNumber, meta })).catch(() => ({ tailNumber: ac.tailNumber, meta: null }))
        );
        const metaResults = await Promise.all(metaPromises);
        if (!isMounted) return;

        for (const { tailNumber, meta } of metaResults) {
          if (meta && (meta.pilotName || meta.destination || meta.lastLocation)) {
            info[tailNumber] = info[tailNumber] || {};
            if (meta.pilotName) info[tailNumber].pilotName = meta.pilotName;
            if (meta.destination) info[tailNumber].destination = meta.destination;
            if (meta.lastLocation) info[tailNumber].lastLocation = meta.lastLocation;
          }
        }

        try {
          const missionsResponse = await Promise.race([
            lazyApi.get('/missions', { params: { limit: 200 } }),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 5000)),
          ]) as any;
          if (!isMounted) return;
          const missions = missionsResponse.data?.data || missionsResponse.data || [];
          const now = new Date();
          for (const ac of liveAircrafts) {
            const legsForAircraft: any[] = [];
            for (const mission of missions) {
              const legs = Array.isArray(mission?.Leg) ? mission.Leg : [];
              for (const leg of legs) {
                if (leg?.aircraftId === ac.id) {
                  legsForAircraft.push({ ...leg, __mission: mission });
                }
              }
            }
            if (legsForAircraft.length === 0) continue;

            const todayCandidates = legsForAircraft.filter((leg) => {
              const dep = pickTime(leg, ['offBlock', 'offBlockAt', 'departureOffBlockAt', 'takeoff', 'takeoffAt', 'scheduledDeparture', 'departureTime', 'etd']);
              const arr = pickTime(leg, ['onBlock', 'onBlockAt', 'arrivalOnBlockAt', 'landing', 'landingAt', 'arrivalTime', 'eta']);
              return (dep && isSameLocalDay(dep, now)) || (arr && isSameLocalDay(arr, now)) || leg?.status === 'IN_FLIGHT' || leg?.status === 'ACTIVE';
            });

            const pool = todayCandidates.length > 0 ? todayCandidates : legsForAircraft;
            let bestLeg = pool[0];
            let bestScore = -Infinity;
            for (const leg of pool) {
              const score = scoreLegForAircraft(leg, info[ac.tailNumber], now);
              if (score > bestScore) {
                bestScore = score;
                bestLeg = leg;
              }
            }

            if (!info[ac.tailNumber]) info[ac.tailNumber] = {};
            const entry = info[ac.tailNumber];

            if (!entry.destination) {
              entry.destination = bestLeg.arrivalICAO || bestLeg.to || bestLeg.arrivalName || undefined;
            }

            const crew = resolveCrewNames(bestLeg);
            if (!entry.pilotName && crew.pilotName) entry.pilotName = crew.pilotName;
            if (!entry.pilot && crew.pilotId) entry.pilot = crew.pilotId;
            if (!entry.coPilotName && crew.coPilotName) entry.coPilotName = crew.coPilotName;
            if (!entry.coPilot && crew.coPilotId) entry.coPilot = crew.coPilotId;

            entry.offBlock = pickTime(bestLeg, ['offBlock', 'offBlockAt', 'departureOffBlockAt', 'actualOffBlockAt']);
            entry.takeoff = pickTime(bestLeg, ['takeoff', 'takeoffAt', 'departureTakeoffAt', 'actualTakeoffAt']);
            entry.landing = pickTime(bestLeg, ['landing', 'landingAt', 'arrivalLandingAt', 'actualLandingAt']);
            entry.onBlock = pickTime(bestLeg, ['onBlock', 'onBlockAt', 'arrivalOnBlockAt', 'actualOnBlockAt']);
            entry.legStartAt = pickTime(bestLeg, ['offBlock', 'offBlockAt', 'departureOffBlockAt', 'takeoff', 'takeoffAt', 'scheduledDeparture', 'departureTime']);
            entry.legEndAt = pickTime(bestLeg, ['onBlock', 'onBlockAt', 'arrivalOnBlockAt', 'landing', 'landingAt', 'arrivalTime']);
            entry.source = 'MISSION_LEG';

            // Audit output for requested troubleshooting
            if (ac.tailNumber === '5H-AZH') {
              // eslint-disable-next-line no-console
              console.log('[DashboardAudit][5H-AZH] mission/leg match', {
                selectedLegId: bestLeg?.id,
                status: bestLeg?.status,
                destination: entry.destination,
                pilotName: entry.pilotName,
                coPilotName: entry.coPilotName,
                offBlock: entry.offBlock,
                takeoff: entry.takeoff,
                landing: entry.landing,
                onBlock: entry.onBlock,
                legStartAt: entry.legStartAt,
                legEndAt: entry.legEndAt,
              });
            }
          }
        } catch {
          // Missions optional
        }

        if (isMounted) {
          if (info['5H-AZH']) {
            // eslint-disable-next-line no-console
            console.log('[DashboardAudit][5H-AZH] flightInfo final', info['5H-AZH']);
          }
          setAircraftFlightInfo(info);
        }
      } catch {
        // flight info is optional
      }
    };

    if (liveAircrafts.length > 0 && isMounted) {
      fetchFlightInfo();
      const interval = setInterval(() => {
        if (isMounted) fetchFlightInfo();
      }, 300000);

      return () => {
        isMounted = false;
        clearInterval(interval);
      };
    }

    return () => {
      isMounted = false;
    };
  }, [liveAircrafts, setAircraftFlightInfo]);

  useEffect(() => {
    let isMounted = true;
    const fetchLiveStats = async () => {
      if (!isMounted) return;

      try {
        const [fleet, snapshot] = await Promise.all([
          fleetApi.getAircrafts().catch(() => filteredInitialAircrafts),
          telemetryApi.getSnapshot().catch(() => ({})),
        ]);

        if (!isMounted) return;

        const merged = fleet
          .filter((ac: AircraftType) => ac.tailNumber !== '5H-MAA')
          .map((ac: AircraftType) => ({
            ...ac,
            telemetry: snapshot?.[ac.tailNumber] || ac.telemetry,
          }));
        setLiveAircrafts(merged);
      } catch {
        // ignore polling errors
      }
    };

    fetchLiveStats();
    const interval = setInterval(() => {
      if (isMounted) fetchLiveStats();
    }, 15000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [filteredInitialAircrafts, setLiveAircrafts]);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const res = await api.get('/weather/HTKA');
        const d = res.data?.data;
        const raw: string = d?.rawMETAR || d?.metar || d?.raw || '';
        if (!raw) return;

        const tempMatch = raw.match(/\s(-?\d{2})\/(-?\d{2})\s/);
        const temp = tempMatch ? `${parseInt(tempMatch[1], 10)}°C` : '';

        const windMatch = raw.match(/\b(VRB|\d{3})(\d{2,3})(G\d{2,3})?KT\b/);
        const wind = windMatch ? `${windMatch[1]}/${windMatch[2]}kt` : '';

        let conditions = '';
        if (raw.includes('CAVOK')) conditions = 'CAVOK';
        else {
          const skyMatch = raw.match(/\b(FEW|SCT|BKN|OVC)(\d{3})\b/g);
          conditions = skyMatch ? skyMatch[skyMatch.length - 1] : (raw.includes('SKC') || raw.includes('CLR') ? 'CLR' : '');
        }
        setWeatherData({ temp, wind, conditions });
      } catch {
        // weather is cosmetic
      }
    };

    fetchWeather();
    const iv = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(iv);
  }, [setWeatherData]);
};
